import { Head, router, useForm } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import CategoryCard from "@/components/tryouts/category-card"
import CategoryForm from "@/components/tryouts/category-form"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import type { TryoutsEdit } from "@/types/serializers"

const STATUS_OPTIONS = [
  { value: "open", label: "Abierta" },
  { value: "closed", label: "Cerrada" },
  { value: "paused", label: "Pausada" },
]

export default function TryoutEditPage({ tryout, categories }: TryoutsEdit) {
  const urlParams = new URLSearchParams(window.location.search)
  const showCategoryFormInitial = urlParams.get("new_category") === "true"
  const [showCategoryForm, setShowCategoryForm] = useState(
    showCategoryFormInitial,
  )
  const [showDeleteWarning, setShowDeleteWarning] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
  const [showNoCategories, setShowNoCategories] = useState(false)

  const { data, setData, patch, processing, errors, clearErrors } = useForm({
    tryout: {
      title: tryout.title,
      description: tryout.description,
      venue_name: tryout.venue_name,
      address: tryout.address,
      city: tryout.city,
      region: tryout.region,
      country: tryout.country,
      free: tryout.free,
      requirements: tryout.requirements,
      contact_phone: tryout.contact_phone,
      contact_email: tryout.contact_email,
      status: tryout.status,
    },
  })

  const setField = (
    field: keyof typeof data.tryout,
    value: string | boolean | null,
  ) =>
    setData("tryout", {
      ...data.tryout,
      [field]: value,
    })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (categories.length === 0 && !showNoCategories) {
      setShowNoCategories(true)
      return
    }
    patch(`/pruebas/${tryout.slug}`)
  }

  function handleDeleteCategory(categoryId: number) {
    if (categories.length === 1) {
      setPendingDeleteId(categoryId)
      setShowDeleteWarning(true)
    } else {
      router.delete(`/pruebas/${tryout.slug}/categorias/${categoryId}`)
    }
  }

  function confirmDelete() {
    if (pendingDeleteId) {
      router.delete(`/pruebas/${tryout.slug}/categorias/${pendingDeleteId}`)
      setShowDeleteWarning(false)
      setPendingDeleteId(null)
    }
  }

  function cancelDelete() {
    setShowDeleteWarning(false)
    setPendingDeleteId(null)
  }

  return (
    <AppLayout>
      <Head title={`Editar: ${tryout.title} | KrownFT`} />

      <div className="min-h-svh">
        <div className="mx-auto max-w-2xl px-4 py-6">
          <h1 className="mb-6 text-2xl font-bold">Editar prueba</h1>

          <Card className="mb-6">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    value={data.tryout.title}
                    onChange={(e) => setField("title", e.target.value)}
                  />
                  {errors["tryout.title"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["tryout.title"]}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={data.tryout.description}
                    onChange={(e) => setField("description", e.target.value)}
                    rows={5}
                  />
                </div>

                <div>
                  <Label htmlFor="venue_name">Nombre del predio</Label>
                  <Input
                    id="venue_name"
                    value={data.tryout.venue_name}
                    onChange={(e) => setField("venue_name", e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="address">Dirección</Label>
                  <Input
                    id="address"
                    value={data.tryout.address ?? ""}
                    onChange={(e) =>
                      setField("address", e.target.value || null)
                    }
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="free"
                    checked={data.tryout.free === true}
                    onCheckedChange={(checked) =>
                      setField("free", checked === true)
                    }
                  />
                  <Label htmlFor="free" className="cursor-pointer">
                    La prueba es gratuita
                  </Label>
                </div>

                <div>
                  <Label htmlFor="requirements">Requisitos</Label>
                  <Textarea
                    id="requirements"
                    value={data.tryout.requirements ?? ""}
                    onChange={(e) =>
                      setField("requirements", e.target.value || null)
                    }
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_phone">Teléfono de contacto</Label>
                    <Input
                      id="contact_phone"
                      value={data.tryout.contact_phone ?? ""}
                      onChange={(e) =>
                        setField("contact_phone", e.target.value || null)
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email">Email de contacto</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={data.tryout.contact_email ?? ""}
                      onChange={(e) =>
                        setField("contact_email", e.target.value || null)
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label>Estado</Label>
                  <Select
                    value={data.tryout.status}
                    onValueChange={(v) => setField("status", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {(errors as Record<string, string[]>).base && (
                  <div className="rounded-lg border border-red-500 bg-red-500/10 p-3">
                    <p className="text-sm text-red-500">
                      {(errors as Record<string, string[]>).base?.join(", ")}
                    </p>
                  </div>
                )}

                {showNoCategories && (
                  <div className="rounded-lg border border-yellow-500 bg-yellow-500/10 p-3">
                    <p className="text-sm text-yellow-600">
                      Esta prueba no tiene categorías. Los jugadores no podrán
                      inscribirse hasta que agregues una.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button type="submit" size="sm" disabled={processing}>
                        Guardar de todos modos
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setShowNoCategories(false)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {!showNoCategories && (
                  <Button
                    type="submit"
                    disabled={processing}
                    className="w-full"
                  >
                    Guardar cambios
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Categories management */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Categorías</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCategoryForm(!showCategoryForm)}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showCategoryForm && (
                <div className="border-border mb-4 rounded-lg border p-4">
                  <CategoryForm
                    tryoutSlug={tryout.slug}
                    onCancel={() => setShowCategoryForm(false)}
                    onSuccess={() => {
                      clearErrors()
                      setShowDeleteWarning(false)
                      setPendingDeleteId(null)
                      setShowNoCategories(false)
                    }}
                  />
                </div>
              )}

              {showDeleteWarning && (
                <div className="mb-4 rounded-lg border border-yellow-500 bg-yellow-500/10 p-3">
                  <p className="text-sm text-yellow-600">
                    ¿Estás seguro de eliminar esta categoría? La prueba quedará
                    sin categorías y los jugadores no podrán inscribirse.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={confirmDelete}
                    >
                      Sí, eliminar
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelDelete}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {categories.length > 0 ? (
                <div className="space-y-3">
                  {categories.map((cat) => (
                    <div key={cat.id} className="flex items-start gap-2">
                      <div className="flex-1">
                        <CategoryCard category={cat} />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-red-500 hover:text-red-400"
                        onClick={() => handleDeleteCategory(cat.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Aún no agregaste categorías. Agregá al menos una para que los
                  jugadores puedan inscribirse.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
